import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Ban,
  ExternalLink,
  FileText,
  MapPin,
  Package,
  RefreshCw,
  Truck,
  Wallet,
} from "lucide-react";
import {
  ERROR_CODES,
  MX_STATES,
  PAID_ORDER_STATUSES,
  formatPrice,
  shipmentPartySchema,
  type Order,
  type OrderShipment,
  type ShipmentParty,
  type ShipmentStatus,
  type ShippingQuoteOption,
} from "@nh/shared";
import { GlassPanel } from "@/shared/ui/layout";
import { Alert, Badge, Button, Input, Select, type BadgeVariant } from "@/shared/ui/core";
import { isApiError } from "@/shared/api/client";
import { ConfirmDialog } from "@/shared/ui/ui";
import { useApiErrorMessage } from "@/features/admin/products/useApiErrorMessage";
import { useSedes } from "@/features/admin/sedes/sedes.queries";
import {
  useCancelShipment,
  useCreateShipment,
  useQuoteShipping,
  useRefreshShipment,
  useShippingWallet,
} from "./shipping.queries";
import { fetchLabelUrl, type ShippingQuote } from "./shipping.api";
import { formatOrderDate } from "./format";

/**
 * ═══════════════════════════════════════════════════════════════════
 * El envío de la orden, con Trasso (27-ago).
 *
 * El flujo de generar sigue el contrato de Trasso al pie: COTIZAR
 * (gratis — la dirección completa viaja desde aquí) → elegir una opción
 * → GENERAR (descuenta el wallet; idempotente por cotización). La
 * cotización vence a los 30 minutos y la card lo dice.
 *
 * El PDF de la etiqueta NUNCA se guarda: la URL firmada de Trasso vive
 * 1 hora y trae los datos del destinatario — el botón la pide FRESCA y
 * la abre en el momento.
 * ═══════════════════════════════════════════════════════════════════
 */

const STATUS_LABELS: Record<ShipmentStatus, string> = {
  label_created: "Guía creada",
  picked_up: "Recogido",
  in_transit: "En camino",
  out_for_delivery: "En reparto",
  delivered: "Entregado",
  exception: "Incidencia",
  returned: "Devuelto",
  cancelled: "Cancelada",
};

const STATUS_VARIANTS: Record<ShipmentStatus, BadgeVariant> = {
  label_created: "info",
  picked_up: "indigo",
  in_transit: "indigo",
  out_for_delivery: "indigo",
  delivered: "success",
  exception: "warning",
  returned: "error",
  cancelled: "neutral",
};

export function ShippingCard({ order }: { order: Order }) {
  const { t } = useTranslation();

  /* La guía solo tiene sentido con el pago validado; antes, la card
   * existe pero solo lo dice — sin botones que van a fallar. */
  const pagada = PAID_ORDER_STATUSES.includes(order.status);
  const guiaActiva = order.shipping && order.shipping.status !== "cancelled";

  const wallet = useShippingWallet(pagada || Boolean(order.shipping));
  const sinConfigurar =
    wallet.isError &&
    isApiError(wallet.error) &&
    wallet.error.code === ERROR_CODES.SHIPPING_NOT_CONFIGURED;

  return (
    <GlassPanel className="animate-apple-fade-in p-5 sm:p-6">
      <div className="grid gap-5">
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-ds-ink text-white">
            <Truck className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 className="text-[17px] font-semibold text-ds-ink">{t("Envío")}</h2>
            <p className="text-[13px] text-ds-ink-3">
              {guiaActiva
                ? t("Guía generada con Trasso.")
                : t("La guía se genera con Trasso desde aquí.")}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {order.shipping && (
              <Badge variant={STATUS_VARIANTS[order.shipping.status]}>
                {t(STATUS_LABELS[order.shipping.status])}
              </Badge>
            )}
            {wallet.data && (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-ds-border bg-white px-3 py-1 text-[12px] font-medium text-ds-ink-2">
                <Wallet className="h-3.5 w-3.5 text-ds-ink-3" />
                {t("Wallet: {{saldo}}", {
                  saldo: formatPrice(wallet.data.availableCents, "MXN"),
                })}
              </span>
            )}
          </div>
        </div>

        {/*
          Lo que PAGÓ el comprador en el checkout (13-oct-2026): sirve
          para generar la guía con el mismo servicio y para cuadrar lo
          cobrado contra lo que Trasso descuenta del wallet.
        */}
        {order.shippingCharge && (
          <p className="rounded-2xl border border-ds-border bg-ds-cream-2/40 px-4 py-3 text-[13px] text-ds-ink-2">
            {t("El cliente pagó {{precio}} de envío por {{servicio}}", {
              precio: formatPrice(order.shippingCharge.priceCents, "MXN"),
              servicio: `${order.shippingCharge.carrier} · ${order.shippingCharge.serviceCode}`,
            })}
            {order.shippingCharge.boxName
              ? t(" · caja «{{caja}}»", { caja: order.shippingCharge.boxName })
              : ""}
            {t(" · {{kg}} kg, {{l}}×{{w}}×{{h}} cm.", {
              kg: (order.shippingCharge.package.weightGrams / 1000).toFixed(2),
              l: order.shippingCharge.package.lengthCm,
              w: order.shippingCharge.package.widthCm,
              h: order.shippingCharge.package.heightCm,
            })}
          </p>
        )}

        {sinConfigurar ? (
          <Alert variant="info" title={t("Los envíos con Trasso están apagados")}>
            {t(
              "Falta TRASSO_API_KEY en el servidor. La llave se crea en el panel de Trasso (API keys) con los scopes quotes:read, shipments:write y shipments:read.",
            )}
          </Alert>
        ) : !pagada && !order.shipping ? (
          <p className="text-[13px] leading-relaxed text-ds-ink-3">
            {order.status === "cancelled"
              ? t("Una orden cancelada no lleva envío.")
              : t("Valida el pago para preparar el envío: cada guía descuenta saldo real del wallet.")}
          </p>
        ) : guiaActiva && order.shipping ? (
          <ShipmentView order={order} shipping={order.shipping} />
        ) : (
          <>
            {order.shipping?.status === "cancelled" && (
              <CancelledNote shipping={order.shipping} />
            )}
            <QuoteAndCreate order={order} />
          </>
        )}
      </div>
    </GlassPanel>
  );
}

// ═════════════════════════════════════════════════════════════════════
// La guía viva: rastreo, etiqueta, cancelación
// ═════════════════════════════════════════════════════════════════════

function ShipmentView({ order, shipping }: { order: Order; shipping: OrderShipment }) {
  const { t } = useTranslation();
  const refresh = useRefreshShipment(order.id);
  const cancel = useCancelShipment(order.id);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [labelError, setLabelError] = useState<string | null>(null);
  const [labelPending, setLabelPending] = useState(false);
  const refreshError = useApiErrorMessage(refresh.error);
  const cancelError = useApiErrorMessage(cancel.error);

  /*
   * La URL del PDF se pide al momento: la firmada vive 1 hora. La
   * pestaña se abre ANTES del `await` — abrirla después cae en el
   * bloqueador de popups — y se le pone la URL cuando llega.
   */
  async function abrirEtiqueta() {
    setLabelError(null);
    setLabelPending(true);
    const ventana = window.open("about:blank", "_blank");
    try {
      const url = await fetchLabelUrl(order.id);
      if (ventana) ventana.location.href = url;
      else window.open(url, "_blank");
    } catch (cause) {
      ventana?.close();
      setLabelError(
        cause instanceof Error && cause.message
          ? cause.message
          : t("No se pudo obtener la etiqueta. Intenta de nuevo."),
      );
    } finally {
      setLabelPending(false);
    }
  }

  const puedeCancelar = ["label_created", "picked_up", "in_transit"].includes(
    shipping.status,
  );

  return (
    <div className="grid gap-4">
      <dl className="grid gap-3 rounded-2xl border border-ds-border bg-ds-cream-2/40 p-4 text-[13px] sm:grid-cols-2">
        <div>
          <dt className="font-medium text-ds-ink-3">{t("Paquetería")}</dt>
          <dd className="text-ds-ink">
            {shipping.carrier} · {shipping.serviceCode}
          </dd>
        </div>
        <div>
          <dt className="font-medium text-ds-ink-3">{t("Rastreo")}</dt>
          <dd className="font-mono text-ds-ink">
            {shipping.trackingNumber ?? t("aún sin asignar")}
          </dd>
        </div>
        <div>
          <dt className="font-medium text-ds-ink-3">{t("Costo de la guía")}</dt>
          <dd className="text-ds-ink">{formatPrice(shipping.priceMxnCents, "MXN")}</dd>
        </div>
        <div>
          <dt className="font-medium text-ds-ink-3">{t("Paquete")}</dt>
          <dd className="text-ds-ink">
            {t("{{kg}} kg · {{l}}×{{w}}×{{h}} cm", {
              kg: (shipping.package.weightGrams / 1000).toFixed(2),
              l: shipping.package.lengthCm,
              w: shipping.package.widthCm,
              h: shipping.package.heightCm,
            })}
          </dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="font-medium text-ds-ink-3">{t("Trayecto")}</dt>
          <dd className="text-ds-ink">
            {shipping.sedeName ?? shipping.from.city} → {shipping.to.city},{" "}
            {shipping.to.state} · CP {shipping.to.zip}
          </dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="font-medium text-ds-ink-3">{t("Generada")}</dt>
          <dd className="text-ds-ink">
            {formatOrderDate(shipping.createdAt)}
            {shipping.updatedAt
              ? ` · ${t("último movimiento: {{fecha}}", { fecha: formatOrderDate(shipping.updatedAt) })}`
              : ""}
          </dd>
        </div>
      </dl>

      {shipping.status === "exception" && (
        <Alert variant="warning" title={t("La paquetería reporta una incidencia")}>
          {t("Revisa el rastreo con el número de guía y, si hace falta, contacta a soporte de Trasso.")}
        </Alert>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          leftIcon={<FileText />}
          loading={labelPending}
          onClick={() => void abrirEtiqueta()}
          rightIcon={<ExternalLink />}
        >
          {t("Abrir etiqueta (PDF)")}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<RefreshCw />}
          loading={refresh.isPending}
          onClick={() => refresh.mutate()}
        >
          {t("Actualizar estado")}
        </Button>
        {puedeCancelar && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Ban />}
            onClick={() => setConfirmCancel(true)}
          >
            {t("Cancelar guía")}
          </Button>
        )}
      </div>
      <p className="text-[12px] text-ds-ink-3">
        {t("El rastreo se actualiza cada 30 minutos del lado de Trasso: media hora sin cambios no es una falla.")}
      </p>

      {labelError && (
        <Alert variant="error" onDismiss={() => setLabelError(null)}>
          {labelError}
        </Alert>
      )}
      {refresh.isError && (
        <Alert variant="error" onDismiss={() => refresh.reset()}>
          {refreshError}
        </Alert>
      )}
      {cancel.isError && (
        <Alert variant="error" onDismiss={() => cancel.reset()}>
          {cancelError}
        </Alert>
      )}

      <ConfirmDialog
        open={confirmCancel}
        title={t("¿Cancelar la guía?")}
        description={t(
          "Trasso reembolsa el importe completo al wallet. Ojo: la etiqueta impresa puede seguir viva con la paquetería — destrúyela si ya se imprimió.",
        )}
        confirmLabel={t("Cancelar la guía")}
        cancelLabel={t("Conservarla")}
        loading={cancel.isPending}
        onCancel={() => setConfirmCancel(false)}
        onConfirm={() =>
          cancel.mutate(undefined, { onSettled: () => setConfirmCancel(false) })
        }
      />
    </div>
  );
}

/** La guía anterior quedó cancelada: se dice, y el flujo de generar una
 * nueva aparece debajo. */
function CancelledNote({ shipping }: { shipping: OrderShipment }) {
  const { t } = useTranslation();
  return (
    <Alert
      variant={shipping.liveLabelOnCancel ? "warning" : "info"}
      title={t("La guía anterior se canceló (importe reembolsado al wallet)")}
    >
      {shipping.liveLabelOnCancel
        ? t(
            "Trasso avisó que la etiqueta física puede seguir viva con la paquetería: destruye la que se haya impreso antes de generar otra.",
          )
        : t("Puedes generar una nueva guía aquí abajo.")}
    </Alert>
  );
}

// ═════════════════════════════════════════════════════════════════════
// Cotizar y generar
// ═════════════════════════════════════════════════════════════════════

type PartyDraft = Record<keyof ShipmentParty, string>;

function emptyParty(): PartyDraft {
  return { name: "", phone: "", street: "", city: "", state: "", zip: "", reference: "" };
}

/** Lo que se manda: el borrador validado por el MISMO esquema del contrato. */
function partyFrom(draft: PartyDraft): ShipmentParty | { error: string } {
  const parsed = shipmentPartySchema.safeParse({
    ...draft,
    reference: draft.reference.trim() || undefined,
  });
  if (parsed.success) return parsed.data;
  return { error: parsed.error.issues[0]?.message ?? "Revisa los datos" };
}

function QuoteAndCreate({ order }: { order: Order }) {
  const { t } = useTranslation();
  const quote = useQuoteShipping(order.id);
  const create = useCreateShipment(order.id);
  const quoteError = useApiErrorMessage(quote.error);
  const createError = useApiErrorMessage(create.error);
  const [localError, setLocalError] = useState<string | null>(null);

  /* Las sedes activas: el origen del envío. Elegir una PRELLENA el
   * formulario — los campos siguen editables, porque una sede vieja
   * puede no tener estado/CP capturados. */
  const sedes = useSedes({ page: 1, perPage: 100, status: "active" });
  const [sedeId, setSedeId] = useState("");

  const [from, setFrom] = useState<PartyDraft>(emptyParty);
  const [to, setTo] = useState<PartyDraft>(() => ({
    name: order.customer.name,
    phone: order.customer.phone ?? "",
    street: order.customer.shippingAddress?.street ?? order.customer.address ?? "",
    city: order.customer.shippingAddress?.city ?? "",
    state: order.customer.shippingAddress?.state ?? "",
    zip: order.customer.shippingAddress?.zip ?? "",
    reference: order.customer.shippingAddress?.reference ?? "",
  }));

  const [result, setResult] = useState<ShippingQuote | null>(null);
  const [serviceCode, setServiceCode] = useState("");

  const sedeOptions = useMemo(
    () => [
      { value: "", label: t("Elegir sede de origen…") },
      ...(sedes.data?.items ?? []).map((sede) => ({
        value: sede.id,
        label: `${sede.name} · ${sede.city}`,
      })),
    ],
    [sedes.data, t],
  );

  function elegirSede(id: string) {
    setSedeId(id);
    const sede = sedes.data?.items.find((item) => item.id === id);
    if (!sede) return;
    setFrom({
      name: sede.name,
      phone: sede.phone ?? "",
      street: sede.address,
      city: sede.city,
      state: sede.state ?? "",
      zip: sede.zip ?? "",
      reference: "",
    });
  }

  function cotizar() {
    setLocalError(null);
    const origen = partyFrom(from);
    if ("error" in origen) {
      setLocalError(t("Origen: {{detalle}}", { detalle: origen.error }));
      return;
    }
    const destino = partyFrom(to);
    if ("error" in destino) {
      setLocalError(t("Destino: {{detalle}}", { detalle: destino.error }));
      return;
    }
    quote.mutate(
      { from: origen, to: destino, ...(sedeId ? { sedeId } : {}) },
      {
        onSuccess: (data) => {
          setResult(data);
          // El servicio que pagó el cliente, si sigue en la cotización;
          // si no, la recomendación de Trasso (la primera).
          const pagado = data.options.find(
            (option) => option.serviceCode === order.shippingCharge?.serviceCode,
          );
          setServiceCode(pagado?.serviceCode ?? data.options[0]?.serviceCode ?? "");
        },
      },
    );
  }

  function generar() {
    if (!result || !serviceCode) return;
    create.mutate({ quoteId: result.quoteId, serviceCode });
  }

  return (
    <div className="grid gap-4">
      {!result ? (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <fieldset className="grid content-start gap-3 rounded-2xl border border-ds-border bg-ds-cream-2/40 p-4">
              <legend className="sr-only">{t("Origen del envío")}</legend>
              <h3 className="flex items-center gap-2 text-[14px] font-semibold text-ds-ink">
                <Package className="h-4 w-4 text-ds-ink-3" />
                {t("Origen (sede)")}
              </h3>
              <Select
                aria-label={t("Sede de origen")}
                options={sedeOptions}
                value={sedeId}
                onChange={(event) => elegirSede(event.target.value)}
              />
              <PartyFields value={from} onChange={setFrom} idPrefix="envio-origen" />
            </fieldset>

            <fieldset className="grid content-start gap-3 rounded-2xl border border-ds-border bg-ds-cream-2/40 p-4">
              <legend className="sr-only">{t("Destino del envío")}</legend>
              <h3 className="flex items-center gap-2 text-[14px] font-semibold text-ds-ink">
                <MapPin className="h-4 w-4 text-ds-ink-3" />
                {t("Destino (cliente)")}
              </h3>
              <PartyFields value={to} onChange={setTo} idPrefix="envio-destino" />
            </fieldset>
          </div>

          <datalist id="envio-estados">
            {MX_STATES.map((estado) => (
              <option key={estado} value={estado} />
            ))}
          </datalist>

          <div className="flex flex-wrap items-center gap-3">
            <Button size="sm" loading={quote.isPending} onClick={cotizar}>
              {t("Cotizar envío")}
            </Button>
            <p className="text-[12px] text-ds-ink-3">
              {t("Cotizar es gratis: el paquete se calcula de los productos, con su margen del 20 %.")}
            </p>
          </div>
        </>
      ) : (
        <>
          <div className="grid gap-2">
            <p className="text-[13px] text-ds-ink-2">
              {t("Paquete cotizado: {{kg}} kg · {{l}}×{{w}}×{{h}} cm. Elige el servicio:", {
                kg: (result.package.weightGrams / 1000).toFixed(2),
                l: result.package.lengthCm,
                w: result.package.widthCm,
                h: result.package.heightCm,
              })}
            </p>
            <div className="grid gap-2" role="radiogroup" aria-label={t("Opciones de envío")}>
              {result.options.map((option, index) => (
                <QuoteOptionRow
                  key={`${option.carrier}-${option.serviceCode}`}
                  option={option}
                  recommended={index === 0}
                  checked={serviceCode === option.serviceCode}
                  onSelect={() => setServiceCode(option.serviceCode)}
                />
              ))}
              {result.options.length === 0 && (
                <Alert variant="warning">
                  {t("Trasso no tiene servicios para este trayecto hoy. Revisa las direcciones o intenta más tarde.")}
                </Alert>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              size="sm"
              loading={create.isPending}
              disabled={!serviceCode}
              onClick={generar}
            >
              {t("Generar guía")}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setResult(null);
                quote.reset();
                create.reset();
              }}
            >
              {t("Volver a cotizar")}
            </Button>
            <p className="text-[12px] text-ds-ink-3">
              {t("La cotización vence a los 30 minutos. Generar descuenta el costo del wallet.")}
            </p>
          </div>
        </>
      )}

      {localError && (
        <Alert variant="error" onDismiss={() => setLocalError(null)}>
          {localError}
        </Alert>
      )}
      {quote.isError && (
        <Alert variant="error" onDismiss={() => quote.reset()}>
          {quoteError}
        </Alert>
      )}
      {create.isError && (
        <Alert variant="error" onDismiss={() => create.reset()}>
          {createError}
        </Alert>
      )}
    </div>
  );
}

/** Los seis campos de un extremo del envío, compactos y editables. */
function PartyFields({
  value,
  onChange,
  idPrefix,
}: {
  value: PartyDraft;
  onChange: (next: PartyDraft) => void;
  idPrefix: string;
}) {
  const { t } = useTranslation();
  const set = (key: keyof PartyDraft) => (event: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...value, [key]: event.target.value });

  return (
    <div className="grid gap-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Input
          id={`${idPrefix}-name`}
          label={t("Nombre")}
          value={value.name}
          onChange={set("name")}
        />
        <Input
          id={`${idPrefix}-phone`}
          label={t("Teléfono")}
          value={value.phone}
          onChange={set("phone")}
          inputMode="tel"
        />
      </div>
      <Input
        id={`${idPrefix}-street`}
        label={t("Calle, número y colonia")}
        value={value.street}
        onChange={set("street")}
      />
      <div className="grid gap-3 sm:grid-cols-3">
        <Input
          id={`${idPrefix}-city`}
          label={t("Ciudad")}
          value={value.city}
          onChange={set("city")}
        />
        <Input
          id={`${idPrefix}-state`}
          label={t("Estado")}
          value={value.state}
          onChange={set("state")}
          list="envio-estados"
        />
        <Input
          id={`${idPrefix}-zip`}
          label={t("CP")}
          value={value.zip}
          onChange={set("zip")}
          inputMode="numeric"
          maxLength={5}
        />
      </div>
      <Input
        id={`${idPrefix}-reference`}
        label={t("Referencia (opcional)")}
        value={value.reference}
        onChange={set("reference")}
        placeholder={t("Portón azul, andén 3")}
      />
    </div>
  );
}

function QuoteOptionRow({
  option,
  recommended,
  checked,
  onSelect,
}: {
  option: ShippingQuoteOption;
  recommended: boolean;
  checked: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  return (
    <label
      className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-3 transition ${
        checked
          ? "border-ds-violet-pop bg-ds-violet-pop/5 ring-1 ring-ds-violet-pop/30"
          : "border-ds-border bg-white hover:bg-ds-mist/40"
      }`}
    >
      <input
        type="radio"
        name="opcion-envio"
        className="h-4 w-4 accent-ds-violet-pop"
        checked={checked}
        onChange={onSelect}
      />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2 text-[14px] font-medium text-ds-ink">
          {option.carrier} · {option.serviceCode}
          {recommended && <Badge variant="info">{t("Recomendada")}</Badge>}
        </span>
        {typeof option.etaDays === "number" && (
          <span className="text-[12px] text-ds-ink-3">
            {t("Entrega estimada: {{dias}} día(s)", { dias: option.etaDays })}
          </span>
        )}
      </span>
      <span className="text-[15px] font-semibold text-ds-ink">
        {formatPrice(option.priceMxnCents, "MXN")}
      </span>
    </label>
  );
}
